import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { AuthService } from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { BillOfLadingService } from '../../services/billOfLading.service';
import { PERMISSIONS } from '../../constants/permissions';
import { BillOfLadingDTO } from '../../interfaces/commercial.interface';
import { enumLabel } from '../../utils/garmentsLabels';
import { formatDate } from '../../utils/dateUtils';
import { sortByDateDescFutureLast } from '../../utils/listDefaults';

/** Bill of lading / forwarder register (15.3) — flat header, mirrors ExportLcList's list/
 * paginate shape. Reuses EXP_VIEW/EXP_MANAGE (spec §3 — no new permission catalog entry). */
const BillOfLadingList: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.EXP_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;

    const [rows, setRows] = useState<BillOfLadingDTO[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            setLoading(true);
            const res = await BillOfLadingService.list(institutionId, page, size);
            setRows(res.content);
            setTotal(res.totalElements);
        } catch (e) {
            toast.showApiError(e, 'Failed to load bills of lading');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [page, size]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box sx={{ p: 3 }} data-help-page="commercial.blList.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Bills of Lading</Typography>
                {canManage && (
                    <Button data-help="commercial.blList.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/commercial/bills-of-lading/new')}>
                        New Bill of Lading
                    </Button>
                )}
            </Box>

            {loading ? <CircularProgress /> : (
                <TableContainer component={Paper} data-help-section="commercial.blList.table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="commercial.blList.col.number">B/L Number</TableCell>
                                <TableCell data-help="commercial.blList.col.date">Date</TableCell>
                                <TableCell data-help="commercial.blList.col.type">Type</TableCell>
                                <TableCell data-help="commercial.blList.col.vessel">Vessel</TableCell>
                                <TableCell data-help="commercial.blList.col.loading">Port of Loading</TableCell>
                                <TableCell data-help="commercial.blList.col.discharge">Port of Discharge</TableCell>
                                <TableCell data-help="commercial.blList.col.freight">Freight Terms</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortByDateDescFutureLast(rows, x => x.blDate).map(row => (
                                <TableRow key={row.id} hover sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/commercial/bills-of-lading/${row.id}`)}>
                                    <TableCell>{row.blNumber}</TableCell>
                                    <TableCell>{formatDate(row.blDate)}</TableCell>
                                    <TableCell>{enumLabel(row.blType)}</TableCell>
                                    <TableCell>{row.vesselName ?? '—'}</TableCell>
                                    <TableCell>{row.portOfLoading ?? '—'}</TableCell>
                                    <TableCell>{row.portOfDischarge ?? '—'}</TableCell>
                                    <TableCell>{enumLabel(row.freightTerms)}</TableCell>
                                </TableRow>
                            ))}
                            {rows.length === 0 && (
                                <TableRow><TableCell colSpan={7} align="center">No bills of lading found.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={size}
                        onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
                    />
                </TableContainer>
            )}
        </Box>
    );
};

export default BillOfLadingList;
